const JUDGMENTS = Object.freeze({
    SATISFIED: 'satisfied',
    UNSATISFIED: 'unsatisfied',
    NOT_APPLICABLE: 'not_applicable',
});

const ITEM_RETRY_ERROR_MESSAGES = Object.freeze({
    INPUT_CHANGED: '답변 또는 첨부파일이 변경되어 전체 AI 평가가 필요합니다.',
    ALREADY_REVIEWED: '이미 평가자가 검토를 확정한 항목입니다.',
    RETRY_IN_PROGRESS: '다른 실패 항목을 재평가하고 있습니다. 완료 후 다시 요청해 주세요.',
    ITEM_NOT_RETRYABLE: '기술적으로 실패한 AI 평가만 다시 요청할 수 있습니다.',
    RESULT_CHANGED: '검토 결과가 변경되었습니다. 새로고침 후 다시 요청해 주세요.',
    RETRY_DISPATCH_FAILED: 'AI 평가 요청을 보내지 못했습니다. 잠시 후 다시 시도해 주세요.',
});

// AI 판정 항목의 review: 사람이 확인할 점. 충족 여부(is_satisfied)와 별개 계층이라 판정 옆 배지로만 보인다.
const REVIEW_CODE_LABELS = Object.freeze({
    FILE_UNREADABLE: '첨부 일부 못 읽음',
    NA_ANSWERED: '업체 \'해당없음\' 답변',
    URL_UNREAD: '첨부 URL 못 읽음',
    GROUNDING_ISSUE: '근거 검증 불일치',
    ABSENCE_CONFLICT: '택1 선택지 경합',
    DUPLICATE_CONFLICT: 'AI 응답 결론 엇갈림',
    MACHINE_EVAL_FAILED: '답변 자동 대조 실패',
    NO_EVIDENCE_CITED: '근거 인용 없이 충족',
    VERDICT_UNKNOWN: '판단 보류(옛 기록)',
    RESTORED_CHECKPOINT: '중간 저장본 복원',
});

const FILE_UNREADABLE_REASON_LABELS = Object.freeze({
    size_unknown: '크기 확인 불가',
    too_large: '파일이 너무 큼',
    total_too_large: '전체 용량 초과',
    too_many_files: '파일 수 초과',
    unreadable: '읽을 수 없음',
    some_unreadable: '일부 읽을 수 없음',
    partially_read: '일부만 읽음',
});

function reviewFlags(item) {
    const list = item && Array.isArray(item.review) ? item.review : [];
    return list
        .filter(entry => entry && entry.code)
        .map(entry => {
            const reasons = Array.isArray(entry.reasons)
                ? entry.reasons.map(reason => FILE_UNREADABLE_REASON_LABELS[reason] || reason)
                : [];
            const urls = Array.isArray(entry.url_statuses) ? entry.url_statuses.length : 0;
            return {
                code: entry.code,
                label: REVIEW_CODE_LABELS[entry.code] || `확인 필요 ${entry.code}`,
                note: typeof entry.note === 'string' && entry.note.trim() ? entry.note.trim() : '',
                reasons,
                // 툴팁 한 줄: note · 사유 · URL 개수
                detail: [
                    entry.note,
                    reasons.length ? `사유: ${reasons.join(', ')}` : '',
                    urls ? `URL ${urls}개` : '',
                ].filter(Boolean).join(' · '),
            };
        });
}

// '확인 필요' 가 붙은 항목 수(코드 개수가 아니라 항목 단위)
const countReviewFlagged = items => (Array.isArray(items) ? items : [])
    .filter(item => reviewFlags(item).length > 0).length;

function selectLatestAudit(audits) {
    const list = Array.isArray(audits) ? audits : [];
    return [...list].sort((left, right) => {
        const leftTime = Date.parse((left && (left.updated_at || left.created_at)) || '') || 0;
        const rightTime = Date.parse((right && (right.updated_at || right.created_at)) || '') || 0;
        if (rightTime !== leftTime) return rightTime - leftTime;
        return Number((right && right.id) || 0) - Number((left && left.id) || 0);
    })[0] || null;
}

const categoryCode = item => (
    item && item.reason && item.reason.category && item.reason.category.code
) || null;

const isRetryableAiEvaluationItem = item => categoryCode(item) === 'EVAL_FAILED';

function getAiEvaluationItemState(item, audit, evId) {
    if (audit && audit.status === 'retrying' && Number(audit.retry_target_ev_id) === Number(evId)) {
        return 'retrying';
    }
    if (audit && ['requested', 'processing', 'awaiting_ai'].includes(audit.status)) return 'pending';
    if (audit && audit.status === 'retrying' && audit.retry_target_ev_id == null) return 'pending';
    if (isRetryableAiEvaluationItem(item)) return 'failed';
    // AI 가 평가해 근거(reason)는 냈지만 충족 여부를 정하지 않은 항목 — 백엔드 satisfaction_policy 가
    // 사람 검토로 넘긴 '판정 보류'다(EV_ABSENCE 경합·부적합 슬롯, EV_OVERLOAD/EV_ETC 등).
    // '평가 없음'으로 보이면 불필요한 재요청을 부른다.
    if (item && categoryCode(item) && normalizeJudgment(item) === null) return 'held';
    if (!item || normalizeJudgment(item) === null) return 'missing';
    return 'ready';
}

// AI 평가 완료를 기다리는 폴링 간격(ms). 즉시 실행도 수 분 걸리므로 처음엔 촘촘히, 점점 느슨하게
// 보다가 10분이 지나면 멈춘다(null) — 그 뒤는 '다시 불러오기'로 사용자가 이어 간다(배치는 최대 24시간).
const AUDIT_POLL_LIMIT_MS = 10 * 60 * 1000;
function auditPollDelay(attempt, elapsedMs) {
    if (elapsedMs >= AUDIT_POLL_LIMIT_MS) return null;
    if (attempt <= 15) return 2000;
    if (attempt <= 39) return 5000;
    return 15000;
}

// 해당없음은 문항(답변) 단위라 평가요소별로 켜지 않는다. 한 답변의 AI 결과가 전부 해당없음이면 'on',
// 하나도 아니면 'off', 섞여 있으면 'mixed'(데이터 이상 — 토글로 한쪽으로 맞춘다). AI 결과가 없으면 null.
function groupNotApplicableState(rows) {
    const judged = (Array.isArray(rows) ? rows : []).filter(row => row && row.aiItem && ['ready', 'held'].includes(row.itemState));
    if (!judged.length) return null;
    const naCount = judged.filter(row => row.aiJudgment === JUDGMENTS.NOT_APPLICABLE).length;
    if (naCount === 0) return 'off';
    return naCount === judged.length ? 'on' : 'mixed';
}

// 검토 가능한 항목(ready/held)의 목록 상태. 보류 항목은 평가자가 판단을 저장하기 전까지 'held'.
function rowReviewState(itemState, aiJudgment, finalJudgment, rating) {
    if (itemState === 'held') return rating ? 'corrected' : 'held';
    if (itemState === 'ready') return deriveReviewState(aiJudgment, finalJudgment, rating);
    return itemState;
}

function normalizeItemRetryError(error) {
    const responseData = error && error.response && error.response.data;
    const code = responseData && responseData.code;
    return ITEM_RETRY_ERROR_MESSAGES[code]
        || (responseData && responseData.message)
        || 'AI 평가 재요청 중 오류가 발생했습니다.';
}

function normalizeJudgment(item) {
    if (!item || typeof item !== 'object') return null;
    if (item.not_applicable === true) return JUDGMENTS.NOT_APPLICABLE;
    if (item.is_satisfied === true) return JUDGMENTS.SATISFIED;
    if (item.is_satisfied === false) return JUDGMENTS.UNSATISFIED;
    return null;
}

function ratingToFinalJudgment(aiJudgment, rating) {
    if (!rating || rating.verdict === 'accept') return aiJudgment;
    if (rating.corrected_not_applicable === true) return JUDGMENTS.NOT_APPLICABLE;
    if (rating.corrected_not_applicable === false) {
        if (rating.corrected_satisfied === true) return JUDGMENTS.SATISFIED;
        if (rating.corrected_satisfied === false) return JUDGMENTS.UNSATISFIED;
    }
    if (rating.corrected_satisfied === true) return JUDGMENTS.SATISFIED;
    if (rating.corrected_satisfied === false) return JUDGMENTS.UNSATISFIED;
    return aiJudgment;
}

function deriveReviewState(aiJudgment, finalJudgment, rating) {
    if (!rating) return 'unreviewed';
    return aiJudgment === finalJudgment ? 'approved' : 'corrected';
}

function buildReviewContextSignature(row) {
    if (!row || typeof row !== 'object') return '';
    return JSON.stringify({
        key: row.key || null,
        aiJudgment: row.aiJudgment || null,
        rating: row.rating || null,
    });
}

function normalizeEvidenceRefs(values) {
    if (!Array.isArray(values)) {
        if (values === null || values === undefined) return [];
        values = [values];
    }
    const unique = [...new Set(values.filter(value => (
        value === 'none' || (Number.isInteger(value) && value >= 0)
    )))];
    return unique.sort((left, right) => {
        if (left === 'none') return 1;
        if (right === 'none') return -1;
        return left - right;
    });
}

function toggleEvidenceReference(values, target) {
    const current = normalizeEvidenceRefs(values);
    if (current.includes(target)) return current.filter(value => value !== target);
    return normalizeEvidenceRefs([...current, target]);
}

function buildRatingPayload({
    aiJudgment,
    finalJudgment,
    reasonCode = null,
    reasonPayload = null,
    evidenceRefs = null,
    comment = null,
} = {}) {
    // aiJudgment=null 은 AI 판정 보류 항목 — 평가자 판단은 항상 정정(partial)으로 기록된다.
    if (aiJudgment !== null && !Object.values(JUDGMENTS).includes(aiJudgment)) {
        throw new TypeError('aiJudgment must be a supported judgment or null');
    }
    if (!Object.values(JUDGMENTS).includes(finalJudgment)) {
        throw new TypeError('finalJudgment must be a supported judgment');
    }

    const unchanged = aiJudgment === finalJudgment;
    if (unchanged) {
        return {
            verdict: 'accept',
            error_fields: [],
            comment: null,
            corrected_satisfied: null,
            corrected_not_applicable: null,
            corrected_category_code: null,
            reason_code: null,
            reason_payload: null,
            evidence_ref: null,
        };
    }

    const aiIsNotApplicable = aiJudgment === JUDGMENTS.NOT_APPLICABLE;
    const finalIsNotApplicable = finalJudgment === JUDGMENTS.NOT_APPLICABLE;
    const errorFields = [];
    if (aiIsNotApplicable !== finalIsNotApplicable) errorFields.push('not_applicable');
    if (!finalIsNotApplicable && (aiIsNotApplicable || aiJudgment !== finalJudgment)) {
        errorFields.push('satisfied');
    }

    return {
        verdict: 'partial',
        error_fields: errorFields,
        comment: typeof comment === 'string' && comment.trim() ? comment.trim() : null,
        corrected_satisfied: finalIsNotApplicable
            ? null
            : finalJudgment === JUDGMENTS.SATISFIED,
        corrected_not_applicable: finalIsNotApplicable,
        corrected_category_code: null,
        reason_code: reasonCode || null,
        reason_payload: reasonPayload && Object.keys(reasonPayload).length ? reasonPayload : null,
        evidence_ref: normalizeEvidenceRefs(evidenceRefs).length
            ? normalizeEvidenceRefs(evidenceRefs)
            : null,
    };
}

function summarizeReviews(rows, currentIndex = -1) {
    const list = Array.isArray(rows) ? rows : [];
    const approved = list.filter(row => row.reviewState === 'approved').length;
    const corrected = list.filter(row => row.reviewState === 'corrected').length;
    const unreviewed = list.filter(row => row.reviewState === 'unreviewed').length;
    const failedCount = list.filter(row => row.reviewState === 'failed').length;
    const retryingCount = list.filter(row => row.reviewState === 'retrying').length;
    const pendingCount = list.filter(row => row.reviewState === 'pending').length;
    const missingCount = list.filter(row => row.reviewState === 'missing').length;
    const heldCount = list.filter(row => row.reviewState === 'held').length;
    const completed = approved + corrected;
    let nextUnreviewedIndex = -1;
    if (list.length) {
        for (let offset = 1; offset <= list.length; offset += 1) {
            const index = (Math.max(currentIndex, -1) + offset) % list.length;
            // 판정 보류도 평가자가 판단해야 하므로 '다음 미검토' 대상이다
            if (list[index] && ['unreviewed', 'held'].includes(list[index].reviewState)) {
                nextUnreviewedIndex = index;
                break;
            }
        }
    }
    return {
        total: list.length,
        completed,
        approved,
        corrected,
        unreviewed,
        failedCount,
        retryingCount,
        pendingCount,
        missingCount,
        heldCount,
        percent: list.length ? Math.round((completed / list.length) * 100) : 0,
        nextUnreviewedIndex,
    };
}

function attachmentsForSub(attachments, subId) {
    if (!Array.isArray(attachments) || subId === null || subId === undefined) return [];
    const targetId = String(subId);
    return attachments.filter(attachment => (
        Array.isArray(attachment && attachment.sub_list)
        && attachment.sub_list.some(link => String(link && link.id) === targetId)
    ));
}

function hasExtractedFacts(factsData) {
    return Array.isArray(factsData && factsData.entries)
        && factsData.entries.some(entry => Array.isArray(entry && entry.facts) && entry.facts.length > 0);
}

function getReviewKeyboardAction(event) {
    if (!event || event.repeat || event.isComposing) return null;
    const altOnly = event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
    if (altOnly && event.code === 'KeyN') return 'next-unreviewed';
    const ctrlOnly = event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey;
    if (ctrlOnly && event.code === 'Enter') return 'save-and-next';
    return null;
}

function formatReviewIdentityValue(name, id) {
    const parts = [name, id]
        .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
        .map(value => String(value).trim());
    return parts.join(' · ') || '—';
}

function calculateAnswerScrollTop({
    scrollTop = 0,
    containerTop = 0,
    targetTop = 0,
    paddingTop = 0,
} = {}) {
    const top = Number(scrollTop) + Number(targetTop) - Number(containerTop) - Number(paddingTop);
    return Math.max(0, Number.isFinite(top) ? top : 0);
}

function scrollAnswerContainerToSub(container, subId) {
    if (!container || subId === null || subId === undefined) return false;

    const targetId = String(subId);
    const target = Array.from(container.querySelectorAll('[data-answer-sub-id]'))
        .find(element => element.getAttribute('data-answer-sub-id') === targetId);
    if (!target) return false;

    const view = container.ownerDocument && container.ownerDocument.defaultView;
    const paddingTop = Number.parseFloat(
        view && view.getComputedStyle ? view.getComputedStyle(container).paddingTop : 0,
    ) || 0;
    const top = calculateAnswerScrollTop({
        scrollTop: container.scrollTop,
        containerTop: container.getBoundingClientRect().top,
        targetTop: target.getBoundingClientRect().top,
        paddingTop,
    });

    if (typeof container.scrollTo === 'function') {
        container.scrollTo({top, behavior: 'smooth'});
    } else {
        container.scrollTop = top;
    }
    return true;
}


// ── 수준(등급) 롤업 ────────────────────────────────────────────────────────────
// 평가요소(충족/미충족 코드) → 문항 → 질문. 백엔드 app/models/evaluation_model.py 의
// EvCode.aggr 를 축자 포팅한 것이므로 저장 점수와 같은 규칙으로 계산된다.
// 표시 순서: 해당없음 → 매우미흡 → 미흡 → 보통 → 우수
const LEVEL_ORDER = ['N', 'D', 'C', 'B', 'A'];

function buildEvCodeIndex(evCodeList) {
    const index = {};
    (Array.isArray(evCodeList) ? evCodeList : []).forEach(function (row) {
        if (!row || !row.code) return;
        index[row.code] = {
            code: row.code,
            level: Number(row.level),
            maskCode: row.mask_code || null,
            aggrCode: row.aggr_code || null,
            label: row.description || null,
        };
    });
    return index;
}

// mask 적용 → N(해당없음) 제외 → 최소 level 의 aggr_code.
// 단 최소 level 이 50 이상이면 최대 level 을 쓴다(백엔드 규칙 그대로).
function aggregateEvCodes(codes, index, options) {
    const summary = Boolean(options && options.summary);
    const unique = [];
    (Array.isArray(codes) ? codes : []).forEach(function (code) {
        if (code && unique.indexOf(code) === -1) unique.push(code);
    });
    if (!unique.length) return null;

    let minEv = null;
    let maxEv = null;
    let isAllNa = true;

    unique.forEach(function (code) {
        let ev = index ? index[code] : null;
        if (!ev) return;                                   // 마스터에 없는 코드는 기여하지 않는다
        if (ev.maskCode && !summary) ev = index[ev.maskCode] || ev;   // U → D
        if (ev.aggrCode === 'N') return;
        isAllNa = false;
        if (ev.aggrCode === 'U' && minEv && ev.level === minEv.level) minEv = ev;
        if (minEv === null || ev.level < minEv.level) minEv = ev;
        if (maxEv === null || ev.level > maxEv.level) maxEv = ev;
    });

    if (isAllNa) return 'N';
    if (!minEv) return null;
    if (minEv.level >= 50) return maxEv ? maxEv.aggrCode : null;
    return minEv.aggrCode;
}

// 평가요소 하나의 충족/미충족 수준과 "지금 기여하는 코드".
// judgment 가 없으면(AI 평가 없음) 서버가 이미 계산해 보낸 저장 코드로 보완한다 —
// 빼버리면 집계가 실제보다 좋게 나온다.
function resolveCriterionLevels(criterion, judgment, index) {
    const c = criterion || {};
    const meta = function (code) {
        const ev = code && index ? index[code] : null;
        if (!ev) return null;
        return { code: ev.code, aggrCode: ev.aggrCode, label: ev.label };
    };
    const storedCode = c.logical_ev_code || c.ev_code || null;
    let liveCode;
    if (judgment === 'not_applicable') liveCode = 'N';
    else if (judgment === 'satisfied') liveCode = c.pos_ev_code || null;
    else if (judgment === 'unsatisfied') liveCode = c.neg_ev_code || null;
    else liveCode = storedCode;

    return {
        pos: meta(c.pos_ev_code),
        neg: meta(c.neg_ev_code),
        active: judgment === 'not_applicable' ? 'na'
            : judgment === 'satisfied' ? 'pos'
                : judgment === 'unsatisfied' ? 'neg' : null,
        liveCode: liveCode,
        storedCode: storedCode,
        hasCodes: Boolean(c.pos_ev_code || c.neg_ev_code),
    };
}

// 문항 = 하위 평가요소들의 집계. live(검토 반영) 와 stored(저장본) 를 함께 낸다.
function computeSubLevel(criterionLevels, index) {
    const list = Array.isArray(criterionLevels) ? criterionLevels : [];
    return {
        live: aggregateEvCodes(list.map(function (l) { return l ? l.liveCode : null; }), index),
        stored: aggregateEvCodes(list.map(function (l) { return l ? l.storedCode : null; }), index),
    };
}

// 질문 = 문항 수준들의 집계 + 수준별 문항 개수.
function rollUpQuestionLevels(subCodes, index) {
    const list = Array.isArray(subCodes) ? subCodes : [];
    const counts = {};
    LEVEL_ORDER.forEach(function (code) { counts[code] = 0; });
    let unscored = 0;
    list.forEach(function (code) {
        if (code && Object.prototype.hasOwnProperty.call(counts, code)) counts[code] += 1;
        else unscored += 1;
    });
    return {
        level: aggregateEvCodes(list, index),
        counts: counts,
        total: list.length,
        unscored: unscored,
    };
}


// 문항 단위 "AI 평가 적용" 페이로드. 사람 평가와 같은 저장 경로(saveQeEvaluateResult)에 실린다.
// 검토자가 정정한 판단이 AI 판단보다 우선한다 — 그게 검토의 목적이다.
// 분류는 AI 가 만든 converted_reason 을 옮기되, 코멘트(결과보고서에 실림)는 기존 사람 코멘트를 그대로 둔다 — AI 근거·총평은 넣지 않는다(운영 요청 2026-10-01, 기존 copySub 과 동일).
function buildApplyEvList(rows, judgments, context) {
    const ctx = context || {};
    return (Array.isArray(rows) ? rows : [])
        .map(function (row) {
            return {row: row, judgment: (row && judgments && judgments[row.key]) || (row && row.finalJudgment) || null};
        })
        // 판정 보류는 평가자가 판단을 고른 경우에만 적용한다
        .filter(function (item) {
            return item.row && (item.row.itemState === 'ready' || (item.row.itemState === 'held' && item.judgment));
        })
        .map(function (item) {
            const row = item.row;
            const judgment = item.judgment;
            const reason = (row.aiItem && row.aiItem.converted_reason) || {};
            const entry = {
                ev_id: row.evId,
                target_company_id: ctx.companyId,
                author_id: ctx.authorId,
                audit_category_code: reason.audit_category_code || null,
                comment: (row.criterion && row.criterion.comment) || null,
                not_applicable: judgment === 'not_applicable',
                satisfied: judgment === 'satisfied',
            };
            // 어느 AI 회차에서 온 판정인지 기록 → 학습 데이터 페어링에 쓰인다
            if (row.audit && row.audit.id !== null && row.audit.id !== undefined) {
                entry.ai_audit_id = row.audit.id;
            }
            return entry;
        });
}

module.exports = {
    JUDGMENTS,
    LEVEL_ORDER,
    buildEvCodeIndex,
    aggregateEvCodes,
    resolveCriterionLevels,
    computeSubLevel,
    rollUpQuestionLevels,
    buildApplyEvList,
    ITEM_RETRY_ERROR_MESSAGES,
    REVIEW_CODE_LABELS,
    reviewFlags,
    countReviewFlagged,
    selectLatestAudit,
    isRetryableAiEvaluationItem,
    getAiEvaluationItemState,
    auditPollDelay,
    normalizeItemRetryError,
    normalizeJudgment,
    ratingToFinalJudgment,
    deriveReviewState,
    rowReviewState,
    groupNotApplicableState,
    buildReviewContextSignature,
    normalizeEvidenceRefs,
    toggleEvidenceReference,
    buildRatingPayload,
    summarizeReviews,
    attachmentsForSub,
    hasExtractedFacts,
    getReviewKeyboardAction,
    formatReviewIdentityValue,
    calculateAnswerScrollTop,
    scrollAnswerContainerToSub,
};
